import Vue from "vue";
import VueRouter, { Route, RouteConfig } from "vue-router";

import { LogService } from "@/services";
import { VignetteEndpoints, VignetteRoutes, VignetteVisibility } from "@/types";

import { useSessionStore } from "@/pinia/session";
import Search from "@/views/SearchPage.vue";
import Profile from "@/views/ProfileDetail.vue";
import CommitteeDetail from "@/views/GroupDetail.vue";
import FAQ from "@/views/FAQ.vue";
import Loading from "@/views/LoadingPage.vue";
import NotFound from "@/views/NotFoundPage.vue";
import LoginRedirect from "@/views/LoginRedirect.vue";
import Debug from "@/views/DebugView.vue";
import { useAppStore } from "@/pinia/app";

Vue.use(VueRouter);

const routes: RouteConfig[] = [
  /**
   * Since this is the base path we do need to specify how to handle traffic when it naturally arrives here, but also
   * need to specify that we should not be using it.
   * @deprecated in the sense that we shouldn't use it but since it is the base path it needs to be here.
   */
  {
    path: "/",
    // redirect to /home so that logged-in users go home and logged-out users (via route guard) go to almanac
    // login screen
    redirect: "/home",
    meta: {
      icon: "public",
      label: "Welcome",
      visibility: VignetteVisibility.ALL,
      excludedFromNavBar: false,
      sortOrder: 10,
    },
  },
  {
    path: "/home",
    name: VignetteRoutes.Home,
    component: () =>
      import(/* webpackChunkName: "async-modules" */ "@/views/HomePage.vue"),
    meta: {
      label: "Home",
      icon: "home",
      visibility: VignetteVisibility.MEMBER_ONLY,
      excludedFromNavBar: false,
      sortOrder: 20,
    },
  },
  {
    path: "/browse/state",
    name: VignetteRoutes.BrowseByState,
    component: () => import("@/views/BrowseByState.vue"),
    meta: {
      label: "Browse by State",
      icon: "map",
      visibility: VignetteVisibility.MEMBER_ONLY,
      excludedFromNavBar: false,
      sortOrder: 23,
    },
  },
  {
    path: "/browse/agency",
    name: VignetteRoutes.BrowseByAgency,
    component: () => import("@/views/BrowseByAgency.vue"),
    meta: {
      label: "Browse by Agency",
      icon: "map",
      visibility: VignetteVisibility.MEMBER_ONLY,
      excludedFromNavBar: false,
      sortOrder: 22,
    },
  },
  {
    path: "/browse/caucus-committee",
    name: VignetteRoutes.BrowseByGroup,
    component: () => import("@/views/BrowseByGroup.vue"),
    meta: {
      label: "Browse by Group",
      icon: "map",
      visibility: VignetteVisibility.MEMBER_ONLY,
      excludedFromNavBar: false,
      sortOrder: 24,
    },
  },
  {
    path: "/search",
    redirect: {
      name: VignetteRoutes.SearchCongressional,
    },
    component: Search,
    meta: {
      label: "Search",
      icon: "search",
      visibility: VignetteVisibility.MEMBER_ONLY,
      excludedFromNavBar: false,
      sortOrder: 30,
    },
    children: [
      {
        path: "universal",
        name: VignetteRoutes.SearchUniversal,
        component: () => import("@/components/search/VignetteSearch.vue"),
        meta: {
          label: "Vignette Search",
          icon: "",
          excludedFromNavBar: true,
          sortOrder: 100,
          visibility: VignetteVisibility.MEMBER_ONLY,
          searchLabel:
            "Search all profiles: Elected officials, staffers, agencies, districts, and more",
          searchHint: "",
          transition: "slide-left",
          searchEndpoint: VignetteEndpoints.API_SEARCH_VIGNETTE,
        },
      },
      {
        path: "congressional",
        name: VignetteRoutes.SearchCongressional,
        component: () => import("@/components/search/CongressionalSearch.vue"),
        meta: {
          exportRoute: VignetteEndpoints.API_SEARCH_CONGRESSIONAL_EXPORT,
          label: "Congressional Search",
          icon: "",
          excludedFromNavBar: true,
          sortOrder: 101,
          visibility: VignetteVisibility.MEMBER_ONLY,
          transition: "slide-left",
          searchLabel:
            "Search Congress members, governors, staffers, committees, caucuses, states, and districts",
          searchHint: "",
          searchEndpoint: VignetteEndpoints.API_SEARCH_CONGRESSIONAL,
        },
      },
      {
        path: "federal",
        name: VignetteRoutes.SearchFederal,
        component: () => import("@/components/search/FederalSearch.vue"),
        meta: {
          exportRoute: VignetteEndpoints.API_SEARCH_FEDERAL_EXPORT,
          label: "Federal Search",
          icon: "",
          excludedFromNavBar: true,
          sortOrder: 102,
          visibility: VignetteVisibility.MEMBER_ONLY,
          searchLabel:
            "Search executive branch officials, staffers, and agency offices",
          searchHint: "",
          searchEndpoint: VignetteEndpoints.API_SEARCH_FEDERAL,
          transition: "slide-left",
        },
      },
      {
        path: "state",
        name: VignetteRoutes.SearchState,
        component: () => import("@/components/search/StateLegSearch.vue"),
        meta: {
          exportRoute: VignetteEndpoints.API_SEARCH_STATE_EXPORT,
          label: "State Search",
          icon: "",
          excludedFromNavBar: true,
          sortOrder: 103,
          visibility: VignetteVisibility.MEMBER_ONLY,
          searchLabel:
            "Search state legislators, staffers, states, and districts",
          searchHint: "",
          searchEndpoint: VignetteEndpoints.API_SEARCH_STATE,
          transition: "slide-left",
        },
      },
    ],
  },
  {
    path: "/profile",
    name: VignetteRoutes.ProfileLanding,
    redirect: { name: VignetteRoutes.SearchCongressional },
    meta: {
      label: "Profile",
      icon: "person",
      visibility: VignetteVisibility.MEMBER_ONLY,
      excludedFromNavBar: true,
      searchRoute: VignetteRoutes.SearchUniversal,
      sortOrder: 40,
    },
  },
  {
    path: "/profile/kw/:id(\\d+):name([-/][a-zA-Z-]+)?",
    name: VignetteRoutes.ProfileKW,
    component: Profile,
    meta: {
      exportRoute: VignetteEndpoints.API_PROFILE_KW_EXPORT,
      label: "Profile",
      icon: "person",
      visibility: VignetteVisibility.MEMBER_ONLY,
      excludedFromNavBar: true,
      searchRoute: VignetteRoutes.SearchUniversal,
      sortOrder: 46,
    },
  },
  {
    path: "/profile/:id(\\d+):name([-/][a-zA-Z-]+)?",
    name: VignetteRoutes.Profile,
    component: Profile,
    meta: {
      exportRoute: VignetteEndpoints.API_PROFILE_KW_EXPORT,
      label: "Profile",
      icon: "person",
      visibility: VignetteVisibility.MEMBER_ONLY,
      excludedFromNavBar: true,
      searchRoute: VignetteRoutes.SearchUniversal,
      sortOrder: 45,
    },
  },
  {
    path: "/committee/:id(\\d+):name([-/][a-zA-Z-]+)?",
    name: VignetteRoutes.Committee,
    component: CommitteeDetail,
    props: {
      type: "committee",
    },
    meta: {
      exportRoute: VignetteEndpoints.API_COMMITTEE_EXPORT,
      label: "Committee",
      icon: "none",
      visibility: VignetteVisibility.MEMBER_ONLY,
      excludedFromNavBar: true,
      searchRoute: VignetteRoutes.SearchUniversal,
      sortOrder: 47,
    },
  },
  {
    path: "/caucus/:id(\\d+):name([-/][a-z0-9A-Z-]+)?",
    name: VignetteRoutes.Caucus,
    component: CommitteeDetail,
    props: {
      type: "caucus",
    },
    meta: {
      exportRoute: VignetteEndpoints.API_CAUCUS_EXPORT,
      label: "Caucus",
      icon: "none",
      visibility: VignetteVisibility.MEMBER_ONLY,
      excludedFromNavBar: true,
      searchRoute: VignetteRoutes.SearchUniversal,
      sortOrder: 48,
    },
  },
  {
    path: "/agency/:id(\\d+):name([-/][a-zA-Z-]+)?",
    name: VignetteRoutes.Agency,
    component: () => import("@/views/AgencyDetail.vue"),
    meta: {
      exportRoute: VignetteEndpoints.API_AGENCY_EXPORT,
      label: "Agency",
      icon: "none",
      visibility: VignetteVisibility.MEMBER_ONLY,
      excludedFromNavBar: true,
      searchRoute: VignetteRoutes.SearchUniversal,
      sortOrder: 49,
    },
  },
  {
    path: "/state/:id(\\d+):name([-/][a-zA-Z-]+)?",
    name: VignetteRoutes.State,
    component: () => import("@/views/StateDetail.vue"),
    meta: {
      label: "State",
      icon: "none",
      visibility: VignetteVisibility.MEMBER_ONLY,
      excludedFromNavBar: true,
      searchRoute: VignetteRoutes.SearchUniversal,
      sortOrder: 50,
    },
  },
  {
    path: "/district/:id(\\d+):name([-/][a-zA-Z0-9-]+)?",
    name: VignetteRoutes.District,
    component: () => import("@/views/DistrictDetail.vue"),
    meta: {
      label: "District",
      icon: "none",
      visibility: VignetteVisibility.MEMBER_ONLY,
      excludedFromNavBar: true,
      sortOrder: 51,
      searchRoute: VignetteRoutes.SearchUniversal,
    },
  },
  {
    path: "/briefcase/folder/:folderId(\\d+)",
    name: VignetteRoutes.BriefcaseFolder,
    component: () =>
      import(
        /* webpackChunkName: "async-modules" */ "@/views/BriefcasePage.vue"
      ),
    meta: {
      label: "Briefcase",
      icon: "work",
      visibility: VignetteVisibility.WEB_USER_ONLY,
      excludedFromNavBar: true,
      sortOrder: 50,
    },
  },
  {
    path: "/briefcase",
    name: VignetteRoutes.Briefcase,
    component: () =>
      import(
        /* webpackChunkName: "async-modules" */ "@/views/BriefcasePage.vue"
      ),
    meta: {
      label: "Briefcase",
      icon: "work",
      visibility: VignetteVisibility.WEB_USER_ONLY,
      excludedFromNavBar: false,
      sortOrder: 55,
    },
  },
  {
    path: "/my-account",
    redirect: () => (window.location.href = "/my-account/"),
    meta: {
      label: "My Account",
      icon: "settings",
      visibility: VignetteVisibility.WEB_USER_ONLY,
      excludedFromNavBar: false,
      sortOrder: 65,
    },
  },
  {
    path: "/faq",
    name: VignetteRoutes.Faq,
    component: FAQ,
    meta: {
      label: "FAQ",
      icon: "contact_support",
      visibility: VignetteVisibility.MEMBER_ONLY,
      excludedFromNavBar: false,
      sortOrder: 85,
    },
  },
  {
    path: "/loading",
    name: VignetteRoutes.Loading,
    component: Loading,
    meta: {
      label: "Loading",
      icon: "hourglass_empty",
      visibility: VignetteVisibility.ALL,
      excludedFromNavBar: true,
      sortOrder: 98,
    },
  },
  {
    path: "*",
    name: VignetteRoutes.NotFound,
    component: NotFound,
    meta: {
      label: "Not Found",
      icon: "error",
      visibility: VignetteVisibility.ALL,
      excludedFromNavBar: true,
      sortOrder: 99,
    },
  },
  {
    path: "/forgot",
    name: VignetteRoutes.Forgot,
    meta: {
      icon: "",
      label: "Forgot",
      visibility: VignetteVisibility.ALL,
      excludedFromNavBar: true,
      sortOrder: 102,
    },
  },
  {
    path: "/login-redirect",
    name: VignetteRoutes.LoginRedirect,
    component: LoginRedirect,
    meta: {
      label: "Login",
      icon: "login",
      visibility: VignetteVisibility.PUBLIC_ONLY,
      excludedFromNavBar: false,
      sortOrder: 100,
    },
  },
  {
    path: "/paywall",
    name: VignetteRoutes.PayWall,
    component: () => import("@/views/PayWall.vue"),
    meta: {
      label: "Paywall",
      icon: "lock",
      visibility: VignetteVisibility.PUBLIC_ONLY,
      excludedFromNavBar: false,
      sortOrder: 100,
    },
  },
  {
    path: "/debug",
    name: VignetteRoutes.Debug,
    component: Debug,
    meta: {
      label: "Debug",
      icon: "bug_report",
      visibility: VignetteVisibility.MEMBER_ONLY,
      excludedFromNavBar: true,
      sortOrder: 101,
    },
  },
];

// Set app base url, used in ApiService as well
const basePath = process.env.VUE_APP_BASE_URL || "/";
// Used if origin is not available (IE11 Compatibility View)
let baseOriginFallback = window.location.protocol + "//" + window.location.host;
if (baseOriginFallback.indexOf("undefined") >= 0) {
  baseOriginFallback = "https://www.nationaljournal.com";
}
const baseOrigin = (window.location.origin || baseOriginFallback) + basePath;

LogService.info(
  `origin: '${window.location.origin}', fallback: '${baseOriginFallback}', baseOrigin: '${baseOrigin}'`
);

const router = new VueRouter({
  mode: "history",
  // App served from Django app will not be served from default path, thus need to
  // override base URL retrieved from .env.production (fallback to default for other envs)
  base: basePath,
  scrollBehavior: function (to, from, savedPosition) {
    if (to.hash) {
      return { selector: to.hash, offset: { x: 0, y: 60 } };
    } else if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  },
  routes,
});

const getLapsedRoutes = (routes: RouteConfig[]) => {
  return routes.filter(
    (route) =>
      route.name === VignetteRoutes.MyAccount ||
      route.name === VignetteRoutes.Faq ||
      route.name === VignetteRoutes.NotFound
  );
};

const getIPUserOnlyRoutes = (routes: RouteConfig[]): RouteConfig[] => {
  const matchedRoutes: RouteConfig[] = [];
  routes.forEach((route) => {
    if (
      route.meta?.visibility === VignetteVisibility.MEMBER_ONLY ||
      route.meta?.visibility === VignetteVisibility.ALL
    ) {
      matchedRoutes.push(route);
    }
    if (route.children) {
      matchedRoutes.push(...getIPUserOnlyRoutes(route.children));
    }
  });
  return matchedRoutes;
};

const isIPApprovedRoute = (routes: RouteConfig[], to: Route): boolean => {
  if (to.name != null) {
    const ipRoutes = getIPUserOnlyRoutes(routes);
    return ipRoutes.map((route) => route.name).includes(to.name);
  }
  return false;
};

/**
 *
 * Routing reference:
 *
 * routesStore.getPublicRoutes.map((route) => route.name)
 * ["Loading","NotFound","LoginRedirect","Debug"]
 *
 * routesStore.getPublicNavBarRoutes.map((route) => route.name)
 * ["LoginRedirect"]
 *
 * routesStore.getMemberOnlyRoutes.map((route) => route.name)
 * ["Home","Search","ProfileLanding","Profile","Briefcase","MyAccount","ProfileRequest","Loading","NotFound","Debug"]
 *
 * routesStore.getMemberOnlyNavBarRoutes.map((route) => route.name)
 * ["Home","Search","ProfileLanding","Briefcase","MyAccount","ProfileRequest"]
 *
 * Routing behaviors:
 *
 * Not logged in:
 *
 * + /NotFound, /LoginRedirect, /Debug => next()
 * + /Home, /MyAccount, /Search, /Profile, /ProfileLanding, /Briefcase, /ProfileRequest => ANON: /V/login(redir) NJ-_MBR: /SignUp (placeholder for external Marketo page)
 *
 * Logged in w/ Vignette access:
 *
 * + /NotFound, /Debug => next()
 * + /LoginRedirect => /Home
 * + /Home, /MyAccount, /Search, /Profile, /ProfileLanding, /Briefcase, /ProfileRequest => next()
 */

/**
 *  BE SURE TO CALL next() ONLY ONCE PER beforeEach EXECUTION!!! or return immediately after calling next()
 */
router.beforeEach((to, from, next) => {
  const sessionStore = useSessionStore();
  const appStore = useAppStore();
  LogService.debug(
    `router.beforeEach: from.name: '${from.name}', to.name: '${to.name}', to.fullPath: '${to.fullPath}'`
  );
  // redirect vignette forgot to almanac forgot
  if (to.name === VignetteRoutes.Forgot) {
    let url = window.location.origin;
    window.location.replace("");
    if (url.includes("localhost")) {
      url = "http://localhost:8001";
    }
    window.location.replace(url + "/forgot");
  }
  // redirect a+ contact to almanac contact
  if (to.fullPath === "/contact") {
    let url = window.location.origin;
    window.location.replace("");
    if (url.includes("localhost")) {
      url = "http://localhost:8001";
    }
    window.location.replace(url + "/contact");
  }
  // if going to login redirect just let it through
  if (
    to.name === VignetteRoutes.LoginRedirect ||
    to.name === VignetteRoutes.PayWall
  ) {
    next();
    return;
  }
  /** Intercept app loading state, redirect to Loading view */
  if (to.name !== VignetteRoutes.Loading && appStore.loading) {
    appStore.redirectPath = to.fullPath;
    LogService.debug(`isLoading, to.name: '${to.name}', next:loading`);
    next({ name: VignetteRoutes.Loading });
    return;
  } else if (to.name === VignetteRoutes.Loading) {
    LogService.debug("to:loading, next:next()");
    // gets out of if-else block, calls next() at the bottom of the function
  } else if (
    to.name === VignetteRoutes.MyAccount &&
    sessionStore.hasLapsedSubscription
  ) {
    // band aid fix for lapsed membership, no need to redirect
    // gets out of if-else block, calls next() at the bottom of the function
  } else if (
    to.name === VignetteRoutes.Profile &&
    to.params.id &&
    appStore.unlockedProfiles.indexOf(parseInt(to.params.id)) >= 0
  ) {
    // handle unlocked profiles
    // gets out of if-else block, calls next() at the bottom of the function
  } else if (sessionStore.isLoggedIn && !sessionStore.hasVignetteAccess) {
    next({ name: VignetteRoutes.PayWall });
    return;
  } else if (sessionStore.isIpUser && to.name != null) {
    if (isIPApprovedRoute(routes, to) && sessionStore.hasVignetteAccess) {
      next();
      return;
    }
    /** isIpUser === true, navigate to Home if needed */
    LogService.warn(`ip-user, to: '${to.name}', next:PayWall`);
    next({ name: VignetteRoutes.PayWall });
    return;
  } else if (!sessionStore.isLoggedIn) {
    next({
      name: VignetteRoutes.LoginRedirect,
      query: { next: "/almanacplus" + to.fullPath },
    });
    return;
  } else if (!sessionStore.hasVignetteAccess) {
    // This includes anon users since hasVignetteAccess implied isLoggedIn is true
    // Continue to default next() if target page is visible to lapsed member
    if (
      sessionStore.hasLapsedSubscription ||
      !sessionStore.user?.subscriptionModules.length
    ) {
      /** Capture lapsed members navigation and redirect to My Account for subscription options */
      /** hasVignetteAccess === false, lapsedSubscription === true, navigate to My Account if needed */
      if (
        to.name != null &&
        getLapsedRoutes(routes)
          .map((route) => route.name)
          .includes(to.name)
      ) {
        LogService.warn(
          `lapsedSubscription, to: '${to.name}', next:My Account`
        );
        next({ name: VignetteRoutes.MyAccount });
        return;
      }
    }
    if (to.name !== VignetteRoutes.LoginRedirect) {
      next({
        name: VignetteRoutes.LoginRedirect,
        query: { next: "/almanacplus" + to.fullPath },
      });
      return;
    }
    // Continue to default next() if target is a member only page
  } else {
    /** hasVignetteAccess === true, redirect if requested a public page */
    if (
      to.name != null &&
      to.meta?.visibility === VignetteVisibility.PUBLIC_ONLY
    ) {
      LogService.debug(`hasAccess, requested '${to.name}', next:Home`);
      next({ name: VignetteRoutes.Home });
      return;
    }
  }

  /** All good and no need to redirect anywhere, keep going to target */
  next();
});

export default router;
